import { useLocalization } from '@experiences/locales';
import { ApolloThemeProvider } from '@experiences/theme';
import {
    useRouteResolver,
    useShowDialog,
} from '@experiences/util';
import ErrorIcon from '@mui/icons-material/Error';
import SettingsIcon from '@mui/icons-material/Settings';
import {
    Box,
    Button,
    Tooltip,
    Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import {
    ApDataGridColumn,
    ApDataGridFooter,
    ApDataGridHeader,
    ApDataGridHeaderButton,
    ApDataGridRowActions,
    ApDataGridRowButton,
    ApDataGridWrapper,
    PortalAlertBar,
} from '@uipath/portal-shell-react';
import { capitalize } from 'lodash';
import React, {
    useCallback,
    useMemo,
    useState,
} from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import useSWR, { mutate } from 'swr';

import { notificationType } from '../../common/constants/Constant';
import * as RouteNames from '../../common/constants/RouteNames';
import { useGetSetting } from '../../common/hooks/useGetSetting';
import { useUiSnackBar } from '../../common/hooks/useUiSnackBar';
import type { IReferenceToken } from '../../common/interfaces/cis/profile';
import type { IReferenceTokenFilterOpts } from '../../services/identity/ReferenceTokenService';
import {
    deleteReferenceTokens,
    getAllReferenceTokens,
    referenceTokenUrl,
} from '../../services/identity/ReferenceTokenService';
import {
    accountGlobalId,
    isAdminSelector,
} from '../../store/selectors';
import {
    getRelativeTime,
    timestampToDateTime,
} from '../../util/DateUtil';

const useStyles = makeStyles(theme =>
    createStyles({
        allTokens: {
            fontWeight: 600,
            fontSize: '14px',
            lineHeight: '20px',
        },
        expiredDate: {
            display: 'inline-flex',
            flexDirection: 'row',
            alignItems: 'center',
            color: theme.palette.semantic.colorErrorIcon,
        },
    }),

);

const ExternalApplicationsPATPageComponent: React.FC = () => {
    const { formatMessage: translate } = useIntl();
    const classes = useStyles();

    const history = useHistory();
    const getRoute = useRouteResolver();

    const isAdmin = useSelector(isAdminSelector);
    const partitionGlobalId = useSelector(accountGlobalId);
    const locale = useLocalization();

    const createNotification = useUiSnackBar();

    const [ pagination, setPagination ] = useState<IReferenceTokenFilterOpts>({
        userSearchTerm: '',
        top: 25,
        skip: 0,
        sortBy: 'Expiration',
        sortOrder: 'desc',
    });

    const {
        data: referenceTokens, mutate: mutateReferences, error,
    } = useSWR<{ totalCount: number; results: IReferenceToken[] }>(
        [ referenceTokenUrl, partitionGlobalId, pagination ],
        getAllReferenceTokens,
    );

    const createDialog = useShowDialog();

    const { data: settings } = useGetSetting([ 'ReferenceTokensEnabled' ]);
    const referenceTokensEnabled = useMemo(() =>
        settings?.find(setting => setting.key === 'ReferenceTokensEnabled')?.value?.toLowerCase() === 'true'
    , [ settings ]);

    const goToSettings = useCallback(() => {
        history.push(getRoute(RouteNames.ExternalApplicationsPATSettings));
    }, [ getRoute, history ]);

    const onChangePagination = useCallback(({
        filter, sort, pageIndex, pageSize, searchTerm,
    }) => {
        const newPaginationState: IReferenceTokenFilterOpts = {
            userSearchTerm: searchTerm ?? '',
            top: pageSize ?? 25,
            skip: (pageIndex ?? 0) * (pageSize ?? 25),
            sortBy: capitalize(sort?.[0].field) || 'Expiration',
            sortOrder: sort?.[0].direction === 'asc' ? 'asc' : 'desc',
        };
        setPagination(newPaginationState);
        mutate([
            referenceTokenUrl,
            partitionGlobalId,
            newPaginationState,
        ]);
    }, [ partitionGlobalId ]);

    const openRevokeDialog = useCallback(async (rows: IReferenceToken[] | IReferenceToken) => {
        const selectedTokens = Array.isArray(rows) ? rows.map(row => row) : [ rows ];

        const proceed = await createDialog({
            title: translate({ id: 'CLIENT_REVOKE_TOKEN' }),
            body: <Box>
                <Typography style={{ marginBottom: '20px' }}>
                    {translate(
                        {
                            id: selectedTokens.length === 1
                                ? 'CLIENT_SHOULD_REVOKE_TOKEN'
                                : 'CLIENT_SHOULD_REVOKE_MULTIPLE_TOKENS',
                        },
                    )}
                </Typography>
                <Typography>
                    {translate({ id: 'CLIENT_ARE_YOU_SURE_YOU_WANT_TO_REVOKE' })}
                </Typography>
            </Box>,
            icon: 'warning',
            showCancel: true,
            primaryButtonText: translate({ id: 'CLIENT_REVOKE' }),
        });
        if (proceed) {
            const tokenIds = selectedTokens.map(token => token.id);
            try {
                await deleteReferenceTokens(partitionGlobalId, tokenIds);
                createNotification(translate({ id: 'CLIENT_SUCCESSFULLY_REVOKED_TOKENS' }), notificationType.SUCCESS);
                mutateReferences();
            } catch (e) {
                createNotification(translate({ id: 'CLIENT_FAILED_TO_REVOKE_TOKENS' }), notificationType.ERROR);
            }
        }
    }, [ createDialog, createNotification, mutateReferences, partitionGlobalId, translate ]);

    return <Box>
        <div style={{ marginTop: '12px' }} />
        <PortalAlertBar cancelable={false}>
            {translate({ id: 'CLIENT_PERSONAL_ACCESS_TOKEN_DISCLAIMER' })}
        </PortalAlertBar>
        <Typography style={{
            marginTop: '16px',
            marginBottom: '24px',
        }}>
            {translate({ id: 'CLIENT_PERSONAL_ACCESS_TOKEN_DESCRIPTION' })}
        </Typography>
        <Typography className={classes.allTokens}>
            {translate({ id: 'CLIENT_ALL_TOKENS' }, { number: referenceTokens?.totalCount ?? 0 })}
        </Typography>
        <ApDataGridWrapper<IReferenceToken>
            data={referenceTokens?.results ?? []}
            initialFilters={{
                sort: [
                    {
                        field: 'expiration',
                        direction: 'desc',
                        title: translate({ id: 'CLIENT_EXPIRY_DATE' }),
                    },
                ],
            }}
            loading={!referenceTokens && !error}
            customTemplateProviders={[ { provider: ApolloThemeProvider } ]}
            refreshable
            refresh={() => mutateReferences()}
            onChange={onChangePagination}
            disableSelectionByEntry={(entry) => !referenceTokensEnabled
                ? translate({ id: 'CLIENT_REFERENCE_TOKENS_DISABLED' })
                : null}
            dataCy="ap-data-grid-pat"
        >
            <ApDataGridHeader<IReferenceToken> search>
                {isAdmin && (
                    [
                        <ApDataGridHeaderButton<IReferenceToken>
                            id="tokenSettings"
                            key="tokenSettings"
                            type="main"
                            render={() => <Button
                                color="primary"
                                variant="text"
                                onClick={goToSettings}
                                startIcon={<SettingsIcon />}
                                data-cy="ap-grid-token-settings"
                            >
                                {translate({ id: 'CLIENT_SETTINGS' })}
                            </Button>}
                        />,
                        <ApDataGridHeaderButton<IReferenceToken>
                            id="revokeTokens"
                            key="revokeTokens"
                            type="action"
                            icon="delete"
                            color="warn"
                            buttonType="mat-flat-button"
                            text={translate({ id: 'CLIENT_REVOKE' })}
                            label={translate({ id: 'CLIENT_REVOKE' })}
                            onClick={(rows) => openRevokeDialog(rows ?? [])}
                            dataCy="ap-grid-revoke-tokens"
                        />,
                    ]
                )}
            </ApDataGridHeader>

            <ApDataGridColumn<IReferenceToken>
                property="user.displayName"
                title={translate({ id: 'CLIENT_OWNER' })}
                resizable={false}
                searchable
            />

            <ApDataGridColumn<IReferenceToken>
                property="description"
                title={translate({ id: 'CLIENT_NAME' })}
                resizable={false}
                searchable
                sortable
            />

            <ApDataGridColumn<IReferenceToken>
                property="lastUsed"
                title={translate({ id: 'CLIENT_LAST_IN_USE' })}
                searchable
                resizable={false}
                render={(item) => (
                    <Tooltip title={timestampToDateTime(item.lastUsed)}>
                        <Typography>
                            {item.lastUsed ? getRelativeTime(locale)(+new Date(item.lastUsed)) : translate({ id: 'CLIENT_NEVER' })}
                        </Typography>
                    </Tooltip>
                )}
            />

            <ApDataGridColumn<IReferenceToken>
                property="scopes"
                title={translate({ id: 'CLIENT_SCOPES' })}
                searchable
                resizable={false}
                render={(item) => {
                    const concatenatedString = item.scopes.join(', ');
                    return <Tooltip title={concatenatedString}>
                        <Typography>
                            {concatenatedString}
                        </Typography>
                    </Tooltip>;
                }}
            />

            <ApDataGridColumn<IReferenceToken>
                property="expiration"
                title={translate({ id: 'CLIENT_EXPIRY_DATE' })}
                searchable
                sortable
                sort="desc"
                resizable={false}
                render={(item) => {
                    const convertedDate = +(new Date(item.expiration));
                    if ((convertedDate - +(new Date())) < 0) {
                        return <Typography className={classes.expiredDate}>
                            <ErrorIcon style={{ marginRight: '4px' }} />
                            {translate({ id: 'CLIENT_EXPIRED' })}
                        </Typography>;
                    }

                    return <Typography>
                        {new Date(item.expiration).toLocaleDateString(locale, {
                            month: 'short',
                            year: 'numeric',
                            day: 'numeric',
                        })}
                    </Typography>;
                }}
            />

            {isAdmin && <ApDataGridRowActions>
                <ApDataGridRowButton<IReferenceToken>
                    id='revokeToken'
                    render={(row, _index) => <Tooltip title={referenceTokensEnabled
                        ? translate({ id: 'CLIENT_REVOKE' })
                        : translate({ id: 'CLIENT_REFERENCE_TOKENS_DISABLED' })}>
                        <Button
                            disabled={!referenceTokensEnabled}
                            color="primary"
                            variant="text"
                            onClick={() => openRevokeDialog(row)}
                            data-cy="revokeToken"
                        >
                            {translate({ id: 'CLIENT_REVOKE' })}
                        </Button>
                    </Tooltip>}
                />
            </ApDataGridRowActions>}

            <ApDataGridFooter
                pageSizes={[ 5, 10, 25, 50 ]}
            />
        </ApDataGridWrapper>
    </Box>;
};

export default ExternalApplicationsPATPageComponent;
